import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box/Box";
import {
  Avatar,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import GenieText from "../../components/core/genie-text/GenieText";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import GenieSpinner from "../../components/core/genie-spinner/GenieSpinner";
import { useNavigate, useSearchParams } from "react-router-dom";
import useCreateUser from "../../queries/routes/useCreateUser";
import { purchasingRedirect } from "../../utils/redirects";
import useCreateOrg from "../../queries/routes/useCreateOrg";

function CreateOrg() {
  const navigate = useNavigate();
  // const [searchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const {
    data: user,
    isLoading: isUserLoading,
    mutate: createUser,
  } = useCreateUser();

  const { mutate: createOrg, isLoading: isCreateUserLoading } = useCreateOrg();

  useEffect(() => {
    getAccessTokenSilently().then(() => {
      createUser();
    });
  }, [getAccessTokenSilently, createUser]);

  const defaultValues = {
    name: "",
    selected_type: "Buyer",
    terms: false,
    email: false,
  };

  const [formValues, setFormValues] = useState(defaultValues);

  useEffect(() => {
    if (user && user.organizations && user.organizations.length > 0) {
      console.log("user.organizations", user.organizations);
      // Check for invite_org_id in localStorage first
      const inviteOrgId = localStorage.getItem('invite_org_id');
      if (inviteOrgId) {
        console.log("redirecting to invited storefront", inviteOrgId);
        navigate(`/storefront/${inviteOrgId}`);
        // Clear the invite_org_id from localStorage after redirecting
        localStorage.removeItem('invite_org_id');
        return;
      }

      // Otherwise, use existing logic
      const buyerOrg = user.purchasing_organizations.find((org) => {
        return org.organization.selected_type === "Buyer";
      });
      if (buyerOrg) {
        console.log("buyerOrg", buyerOrg);
        navigate(purchasingRedirect(user));
      } else {
        navigate(`/app/${user.organizations[0].id}/welcome`);
      }
    }
  }, [navigate, user]);

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: checked,
    });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    createOrg({
      selected_type: formValues.selected_type,
      name: formValues.name,
    });
  };

  if (isUserLoading) {
    return <GenieSpinner />;
  }

  return (
    <Box display={"flex"} height={"100vh"} width={"100vw"}>
      <Box
        sx={{
          flexBasis: 1000,
          marginLeft: "5%",
          marginRight: "5%",
        }}
        maxWidth={400}
        height={"100%"}
        display={"flex"}
        alignItems={"center"}
      >
        <Box mr={1}>
          <img
            height={"40px"}
            src={`/assets/genie_logo.svg`}
            alt={"Genie Logo"}
          />

          <form onSubmit={handleSubmit}>
            <Box mt={2}>
              <Grid container>
                <Grid item xs={12}>
                  {user && user.email && (
                    <Box my={4} display={"flex"} alignItems={"center"}>
                      <Box width={"100%"}>
                        <TextField
                          id="email"
                          label="Your email"
                          variant="outlined"
                          required
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <Box mr={2}>
                                <Avatar src={user.picture} alt={user.name} />
                              </Box>
                            ),
                          }}
                          fullWidth
                          name="name"
                          value={user.email}
                          onChange={handleInputChange}
                        />
                      </Box>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Box mb={4}>
                    <TextField
                      id="name"
                      label="Your business name"
                      variant="outlined"
                      required
                      fullWidth
                      focused
                      autoFocus
                      name="name"
                      value={formValues.name}
                      onChange={handleInputChange}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box mb={4}>
                    <FormControl fullWidth>
                      <InputLabel id="account-type-select">
                        You are a
                      </InputLabel>
                      <Select
                        labelId="account-type-select"
                        id="account-type-select"
                        label="You are a"
                        name="selected_type"
                        value={formValues.selected_type}
                        onChange={handleInputChange}
                      >
                        <MenuItem value={"Merchant"}>Merchant</MenuItem>
                        <MenuItem value={"Buyer"}>
                        Buyer
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Grid item xs={12}>
                    <Box mb={4}>
                      <Box display={"flex"} alignItems={"flex-end"}>
                        <Checkbox
                          id="agree-terms"
                          name={"terms"}
                          value={formValues.terms}
                          defaultChecked
                          inputProps={{ "aria-label": "controlled" }}
                          onChange={handleCheckboxChange}
                        />
                        <GenieText subdued paragraph fontSize={"tiny"}>
                          By signing up, you agree to our Terms and privacy
                          policy.
                        </GenieText>
                      </Box>
                      <Box display={"flex"} alignItems={"flex-end"}>
                        <Checkbox
                          id="send-emails"
                          name={"email"}
                          defaultChecked
                          value={formValues.email}
                          inputProps={{ "aria-label": "controlled" }}
                          onChange={handleCheckboxChange}
                        />
                        <GenieText subdued fontSize={"tiny"}>
                          Yes, please send me transactional emails related to my
                          account. I understand that I can opt-out at anytime.
                        </GenieText>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box mb={4}>
                    <Button
                      variant={"outlined"}
                      size={"large"}
                      type={"submit"}
                      disabled={formValues.name === "" || isCreateUserLoading}
                      fullWidth
                    >
                      Finish Creating Your Account
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </form>
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          backgroundSize: "cover",
          backgroundPosition: "center",
          height: "100%",
          backgroundImage: `url("/assets/account.jpg")`,
        }}
      />
    </Box>
  );
}

export default withAuthenticationRequired(CreateOrg, {
  onRedirecting: () => <GenieSpinner />,
});
